// @flow

import LngLat from '../geo/lng_lat';
import type {LngLatLike} from '../geo/lng_lat';
import urls from '../util/urls';
import {Common} from '../util/enums';
import {transformRequestPost, getApiTokenOrKey} from '../util/util';
import type {RequestParameters} from '../util/ajax';
import type {RegionEnum, VehicleTypeEnum, RouteTypeEnum, RouteOptimizationEnum,
    HazMatTypeEnum, DistanceUnitEnum, TruckUnitEnum, FuelUnitEnum,
    TruckConfigEnum, TollCurrencyEnum, DataVersionEnum, ReportTypeEnum,
    TrailerTypeEnum, EmissionTypeEnum, ClassOverrideEnum,
    ETAETDTypeEnum, DateOptionEnum, DayOfWeekEnum,
    TimeZoneEnum, DataRegionEnum, TollRoadsTypeEnum, TunnelCategoryEnum,
    FuelTypeEnum, SideOfStreetAdherenceEnum, HoSScheduleTypeEnum,
    DisplayRestrictionsTypeEnum, ZoneTypeEnum, RoadTypeEnum,
    RailStationFormatEnum, RailRoutePreferenceEnum, RailDistanceUnitEnum} from '../util/enums';
import type {CameraOptions, AnimationOptions} from './camera';
import type {Callback} from '../types/callback';
import config from '../util/config';
import type {DragRouteOptions} from '../ui/drag_route';
import type {ArrowOptions}  from './route_arrow';
import type {IconOptions} from './route_icon';
import type {RouteWidth} from './route_width';

/**
 * Coordinates - service side type
 *  @private
 */
export class CoordinatesStruct {
    Lat: string;
    Lon: string;
    constructor(lngLat: LngLatLike) {
        //use LngLatLike and convert to handle all cases
        const _lngLat = LngLat.convert(lngLat);
        this.Lat = _lngLat.lat.toString();
        this.Lon = _lngLat.lng.toString();
    }

    static transformLngLat(lngLat: LngLatLike): CoordinatesStruct {
        return new CoordinatesStruct(lngLat);
    }
}

export type Coordinates = CoordinatesStruct;

/**
 * Address - service side type
 * @private
 */
export class AddressStruct {
    StreetAddress: string;
    City: string;
    State: string;
    Zip: string;
    County: string;
    Country: string;
    SPLC: string;

    constructor(address: Object) {
        this.StreetAddress = address.StreetAddress || address.streetAddress;
        this.City = address.City || address.city;
        this.State = address.State || address.state;
        this.Zip = address.Zip || address.zip;
        this.County = address.County || address.county;
        this.Country = address.Country || address.country;
        this.SPLC = address.SPLC || address.splc;
    }
}

export type Address = AddressStruct;

/**
 * Location - service side type
 *  @private
 */
export class LocationStruct {
    Coords: Coordinates;
    Address: Address;
    Label: string;

    constructor(options: Object) {
        this.Coords = options.Coords || options.coords;
        if (options.Address || options.address) {
            this.Address = (options.Address) ? new AddressStruct(options.Address) : new AddressStruct(options.address);
        }
        this.Label = options.Label || options.label;
    }
}

export type Location = LocationStruct;

/**
 * Cost - service side type
 * @private
 */
export class CostsStruct {
    CostOfStop: number;
    HoursPerStop: number;
    Loaded: boolean;
    OnDuty: boolean;
    UseOrigin: boolean;

    constructor(options: Object) {
        this.CostOfStop = options.CostOfStop || options.costOfStop;
        this.HoursPerStop = options.HoursPerStop || options.hoursPerStop;

        if ((options.Loaded !== undefined) || (options.loaded !== undefined)) {
            this.Loaded = (options.Loaded !== undefined) ? options.Loaded : options.loaded;
        }
        if ((options.OnDuty !== undefined) || (options.onDuty !== undefined)) {
            this.OnDuty = (options.OnDuty !== undefined) ? options.OnDuty : options.onDuty;
        }
        if ((options.UseOrigin !== undefined) || (options.useOrigin !== undefined)) {
            this.UseOrigin = (options.UseOrigin !== undefined) ? options.UseOrigin : options.useOrigin;
        }
    }

}

export type Costs = CostsStruct;

/**
 * Stop location - service side type
 *  @private
 */
export class StopLocationStruct extends LocationStruct {
    ID: string;
    Costs: Costs;
    IsViaPoint: boolean;
    constructor(options: Object) {
        super(options);
        this.ID = options.ID || options.id || options.Id;
        if ((options.IsViaPoint !== undefined) || (options.isViaPoint !== undefined)) {
            this.IsViaPoint = (options.IsViaPoint !== undefined) ? options.IsViaPoint : options.isViaPoint;
        }
        if (options.Costs || options.costs) {
            this.Costs = (options.Costs) ? new CostsStruct(options.Costs) : new CostsStruct(options.costs);
        }
    }
}

/**
 * Represents a road speed based on road category. - service side type
 *
 * @typedef {Object} RoadSpeedBase
 * @property {Common.RoadType} roadCategory
 * @property {number} speed
 */
export type RoadSpeedBase = {
    roadCategory: RoadTypeEnum,
    speed: number
}

export type StopLocation = StopLocationStruct;

/**
 * Stop options used to provide more information about each stop.
 *
 * Note: When using StopOptions, the requestMethod parameter of RouteOptions will be ignored and the request type will be POST.
 *
 * @typedef {Object} StopOptions
 * @property {string} id String used for the id of the stop.
 * @property {string} label String used for the label of the stop. This label will appear in reports.
 * @property {LngLatLike} lngLat Location of the stop (required).
 * @property {Object} address Address object of the stop.
 * @property {string} address.streetAddress
 * @property {string} address.city
 * @property {string} address.state
 * @property {string} address.zip
 * @property {string} address.county
 * @property {string} address.country
 * @property {string} address.splc
 * @property {Object} costs Object detailing cost information related to the stop
 * @property {number} costs.costOfStop
 * @property {number} costs.hoursPerStop
 * @property {boolean} costs.loaded
 * @property {boolean} costs.onDuty
 * @property {boolean} costs.useOrigin
 * @property {boolean} isViaPoint
 */
export type StopOptions = {
    lngLat: LngLatLike;
    address?: {
        streetAddress?: string;
        city?: string;
        state?: string;
        zip?: string;
        county?: string;
        country?: string;
        splc?: string;
    },
    id?: string;
    costs?: {
        costOfStop?: number;
        hoursPerStop?: number;
        loaded?: boolean;
        onDuty?: boolean;
        useOrigin?: boolean;
    };
    isViaPoint?: boolean;
    label?: string;
}

export type RouteOptionsBase = {
    vehicleType?: VehicleTypeEnum;
    routeType?: RouteTypeEnum;
    routeOptimization?: RouteOptimizationEnum;
    /* @deprecated */
    tollDiscourage?: boolean;
    tollRoads?: TollRoadsTypeEnum;
    afSetIDs?: Array<number>;
    afSetNames?: Array<string>;
    bordersOpen?: boolean;
    overrideRestrict?: boolean;
    highwayOnly?: boolean;
    hazMatType?: HazMatTypeEnum;
    hazMatTypes?: Array<HazMatTypeEnum>;
    tunnelCategories?: Array<TunnelCategoryEnum>;
    customRoadSpeeds?: Array<RoadSpeedBase>;
    distanceUnits?: DistanceUnitEnum;
    // TruckCfg
    trkLCV?: boolean;
    trkUnits?: TruckUnitEnum;
    trkHeight?: string | number; // '13\u00276\"'
    trkLength?: string | number; // '48\u00270\"'
    trkWidth?: string | number; // '96\"',
    trkWeight?: string | number; // '80000'
    trkAxles?: number;
    trkMaxWeightPerAxleGroup?: number;
    trkTireCount?: number;
    ferryDiscourage?: boolean;
    elevLimit?: number;
    hosEnabled?: boolean;
    hosRemDriveTimeUntilBreak?: number;
    hosRemDriveTime?: number;
    hosRemOnDutyTime?: number;
    hosRuleType?: HoSScheduleTypeEnum;
    hosRemCycleDutyTime?: number;

    hosCycleTimeGains?: Array<number>;
    hosLastCycleResetDateTime?: string;
    hosLast24HourBreakDateTime?: string;
    hosRemainingCanadaOnDutyTime?: number;
    hosRemainingDriveTimeUntilBreakDriver2?: number;
    hosRemainingDriveTimeDriver2?: number;
    hosRemainingOnDutyTimeDriver2?: number;
    hosRemainingCycleDutyTimeDriver2?: number;
    hosCycleTimeGainsDriver2?: Array<number>;
    hosLastCycleResetDateTimeDriver2?: string;
    hosLast24HourBreakDateTimeDriver2?: string;
    hosRemainingCanadaOnDutyTimeDriver2?: number;
    hosAlternateHoSRuleType?: number;
    hosRemainingTimeUntilFullDayBreak?: number;
    hosRemainingTimeUntilFullDayBreakDriver2?: number;
    hosRemainingDrivingTimeInWeek?: number;
    hosRemainingOnDutyTimeInDay?: number;
    hosRemainingConsecutiveDrivingTime?: number;
    hosRemainingFortnightlyTime?: number;
    hosCurrentOffDutyTime?: number;
    hosCurrentOffDutyTimeDriver2?: number;
    hosUseCycleReset?: boolean;
    hosTerminalDayStartTime?: string;
    hosUseCustomPlaces?: boolean;
    hosTeamDriving?: boolean;
    // CustomBreakDurations
    hosShortBreakDuration?: number;
    hosEndOfDutyBreakDuration?: number;
    hosCycleResetBreakDuration?: number;
    hosDriverSwitchBreakDuration?: number;
    hosBorderCrossingWaitDuration?: number;
    hosBreakTimeInDayDuration?: number;
    hosBreakTimeInWeekDuration?: number;

    hosMaxAlternateRestStops?: number;
    hosSkipRestStopInsertion?: boolean;
    hosActiveDriver?: number;
    hosMaxDrivingTimeInDay?: number;
    hosMaxOnDutyTimeInDay?: number;
    hosMaxOnDutyTimeInWeek?: number;
    hosAdverseConditionException?: boolean;
    hosPOIStopsReturned?: boolean;
    hosHOSWindowStart?: number;
    hosHOSWindowEnd?: number;

    maintainHeading?: boolean;
    sideOfStreetAdherence?: SideOfStreetAdherenceEnum;
    governorSpeedLimit?: number;
    useAvoidsAndFavors?: boolean;
    useSites?: boolean;
    vehicleProfile?: TruckConfigEnum;
    refrigerated?: boolean;
    fuelType?: FuelTypeEnum;
    fuelConsumption?: number;
    trafficLookaheadDistance?: number;
    avoidState?: Array<string>;
    avoidCountry?: Array<string>;
    favorState?: Array<string>;
    favorCountry?: Array<string>;
    includeTrimblePlaceIDs?: boolean;
    useCrossStreetTurnAvoidance?: boolean;
    congestionZonesOpen?: boolean;
    lowEmissionZonesOpen?: boolean;
    displayRestrictions?: DisplayRestrictionsTypeEnum;
    profileName?: string;
    profileId?: number;
    // TrailerCfg
    trlType?: TrailerTypeEnum;
    trlCount?: number;
    trlMaxHt?: string | number;
    trlMaxWt?: string | number;
    trlMaxAxles?: number;
    // TrailerCfg.Engine
    isHybrid?: boolean;
    emiType?: EmissionTypeEnum;
    minPoll?: boolean;
    overrideClass?: ClassOverrideEnum;
    // RestrictionZones
    congestionZone?: ZoneTypeEnum;
    lowEmissionZone?: ZoneTypeEnum;
    ultraLowEmissionZone?: ZoneTypeEnum;
    hubRouting?: boolean;
};

export type RouteOptions = RouteOptionsBase & {
    // UI options
    routeColor?: string;
    routePathOpacity?: number;
    routeWidth?: RouteWidth | number;
    isDraggable?: boolean;
    dragOptions?: DragRouteOptions;
    originIcon?: IconOptions;
    destinationIcon?: IconOptions;
    stopIcon?: IconOptions;
    frameRoute?: boolean;
    showStops?: boolean;
    frameOptions?: AnimationOptions & CameraOptions;
    showArrows?: boolean;
    arrowOptions?: ArrowOptions;
    isVisible?: boolean;
    styleChangeFrameRoute: boolean;
} & {
    routeId?: string;
    stops: Array<Object>;
    inclFerryDist?: boolean; // Report
    fuelUnits?: FuelUnitEnum; // Report
    truckConfig?: TruckConfigEnum; // SharedOptions/ExtendedOptions and Report
    inclTollData?: boolean; // Report
    fuelEconLoad?: number; // Report
    fuelEconEmpty?: number; // Report
    costPerFuelUnit?: number; // Report
    costGHG?: number; // Report
    costMaintLoad?: number; // Report
    costMaintEmpty?: number; // Report
    costTimeLoad?: number; // Report
    costTimeEmpty?: number; // Report
    tollCurrency?: TollCurrencyEnum;
    exchangeRate?: number; // Report
    tollPlan?: string; // Report
    custRdSpeeds?: boolean; // Report
    citeInt?: number; // Report
    region?: RegionEnum;
    dataVersion?: DataVersionEnum;
    reportType?: Array<ReportTypeEnum>; // Report
    assetId?: string ; // Report
    // language?: LanguageType // Report. Assuming it will be exposed later.
    useEUTollsReport?: boolean; // Report
    useNonNaTolls?: boolean; // Report
    includeRoadClass?: boolean; // Report
    estimatedTimeOpts?: string; // SharedOptions/ExtendedOptions and  // Report
    useTraffic?: boolean; // SharedOptions/ExtendedOptions // and // Report
    includeVehicleRestrictedCleanupPoints?: boolean; // SharedOptions/ExtendedOptions // and // Report
    requestMethod?: string;
}

/**
 * RouteOptionsStruct - server side type
 * @private
 */
type RouteOptionsStruct = {
    VehicleType?: VehicleTypeEnum;
    RoutingType?: RouteTypeEnum;
    RouteOptimization?: RouteOptimizationEnum;
    /* @deprecated */
    TollDiscourage?: boolean;
    TollRoads?: TollRoadsTypeEnum;
    AFSetIDs?: Array<number>;
    AFSetNames?: Array<string>;
    BordersOpen?: boolean;
    OverrideRestrict?: boolean;
    HighwayOnly?: boolean;
    HazMatType?: HazMatTypeEnum;
    HazMatTypes?: Array<HazMatTypeEnum>;
    DistanceUnits?: DistanceUnitEnum;
    TruckCfg?: {
        LCV?: boolean;
        Units?: TruckUnitEnum;
        Height?: string | number;
        Length?: string | number;
        Width?: string | number;
        Weight?: string | number;
        Axles?: number;
        MaxWeightPerAxleGroup?: number;
        TireCount?: number;
    };
    FerryDiscourage?: boolean;
    ElevLimit?: number;
    HoSOptions?: {
        Enabled?: boolean,
        RemainingDriveTimeUntilBreak?: number;
        RemainingDriveTime?: number;
        RemainingOnDutyTime?: number;
        HoSRuleType?: HoSScheduleTypeEnum;
        RemainingCycleDutyTime?: number;
        CycleTimeGains?: Array<number>,
        LastCycleResetDateTime?: string,
        Last24HourBreakDateTime?: string,
        RemainingCanadaOnDutyTime?: number,
        RemainingDriveTimeUntilBreakDriver2?: number,
        RemainingDriveTimeDriver2?: number,
        RemainingOnDutyTimeDriver2?: number,
        RemainingCycleDutyTimeDriver2?: number,
        CycleTimeGainsDriver2?: Array<number>,
        LastCycleResetDateTimeDriver2?: string,
        Last24HourBreakDateTimeDriver2?: string,
        RemainingCanadaOnDutyTimeDriver2?: number,
        AlternateHoSRuleType?: number,
        RemainingTimeUntilFullDayBreak?: number,
        RemainingTimeUntilFullDayBreakDriver2?: number,
        RemainingDrivingTimeInWeek?: number,
        RemainingOnDutyTimeInDay?: number,
        RemainingConsecutiveDrivingTime?: number,
        RemainingFortnightlyTime?: number,
        CurrentOffDutyTime?: number,
        CurrentOffDutyTimeDriver2?: number,
        UseCycleReset?: boolean,
        TerminalDayStartTime?: string,
        UseCustomPlaces?: boolean,
        TeamDriving?: boolean,
        CustomBreakDurations?: {
            ShortBreakDuration?: number,
            EndOfDutyBreakDuration?: number,
            CycleResetBreakDuration?: number,
            DriverSwitchBreakDuration?: number,
            BorderCrossingWaitDuration?: number,
            BreakTimeInDayDuration?: number,
            BreakTimeInWeekDuration?: number,
        },
        MaxAlternateRestStops?: number,
        SkipRestStopInsertion?: boolean,
        ActiveDriver?: number,
        MaxDrivingTimeInDay?: number,
        MaxOnDutyTimeInDay?: number,
        MaxOnDutyTimeInWeek?: number,
        AdverseConditionException?: boolean,
        POIStopsReturned?: boolean,
        HOSWindowStart?: number,
        HOSWindowEnd?: number
    };
    MaintainHeading?: boolean;
    SideOfStreetAdherence?: SideOfStreetAdherenceEnum;
    GovernorSpeedLimit?: number;
    useAvoidsAndFavors?: boolean;
    UseSites?: boolean;
    VehicleProfile?: TruckConfigEnum;
    Refrigerated?: boolean;
    FuelType?: FuelTypeEnum;
    FuelConsumption?: number;
    TrafficLookaheadDistance?: number;
    AvoidState?: Array<string>;
    AvoidCountry?: Array<string>;
    FavorState?: Array<string>;
    FavorCountry?: Array<string>;
    IncludeTrimblePlaceIDs?: boolean;
    useCrossStreetTurnAvoidance?: boolean; // starts with lower case u.
    CongestionZonesOpen?: boolean;
    LowEmissionZonesOpen?: boolean;
    DisplayRestrictionsType?: DisplayRestrictionsTypeEnum;
    profileName?: string; // starts with lower case p.
    profileId?: number;
    TrailerCfg?: {
        TypeOfTrailer?: TrailerTypeEnum;
        Count?: number;
        MaxHeight?: string | number;
        MaxWeight?: string | number;
        MaxAxles?: number;
        Engine?: {
            Hybrid?: boolean;
            EmissionType?: EmissionTypeEnum;
            MinPollutionVeh?: boolean;
        }
    };
    RestrictionZones?: {
        CongestionZone?: ZoneTypeEnum;
        LowEmissionZone?: ZoneTypeEnum;
        UltraLowEmissionZone?: ZoneTypeEnum;
    };
    ClassOverrides?: ClassOverrideEnum;
}

export type DateTimeType = {
    calendarDate?: string;
    dayOfWeek?: DayOfWeekEnum;
    timeOfDay?: string;
    timeZone?: TimeZoneEnum;
}

export type EstimatedTimeOptions = {
    eTAETDType?: ETAETDTypeEnum;
    dateOption?: DateOptionEnum;
    dateAndTime?: DateTimeType;
}

export type SharedOptions = {
    estimatedTimeOptions?: EstimatedTimeOptions;
    useTraffic?: boolean;
    truckStyle?: TruckConfigEnum;
    includeVehicleRestrictedCleanupPoints?: boolean;
}

/**
 * DateTimeTypeStruct - server side type
 * @private
 */
type DateTimeTypeStruct = {
    CalendarDate?: string;
    DayOfWeek?: DayOfWeekEnum;
    TimeOfDay?: string;
    TimeZone?: TimeZoneEnum;
}

/**
 * EstimatedTimeOptionsStruct - server side type
 * @private
 */
type EstimatedTimeOptionsStruct = {
    ETAETD?: ETAETDTypeEnum;
    DateOption?: DateOptionEnum;
    DateAndTime?: DateTimeTypeStruct;
}

/**
 * SharedOptionsStruct - server side type
 * @private
 */
type SharedOptionsStruct = {
    EstimatedTimeOptions?: EstimatedTimeOptionsStruct;
    UseTraffic?: boolean;
    TruckStyle?: TruckConfigEnum;
    IncludeVehicleRestrictedCleanupPoints?: boolean;
}

/**
 * ReportTypeStruct - abstract class, server side type
 * @private
 */
class ReportTypeStruct {
    __type: string;
    constructor(reportType: string) {
        this.__type = `${reportType}ReportType:http:\/\/pcmiler.alk.com\/APIs\/v1.0`;
    }
}

/**
 * RoadReportTypeStruct - server side type
 * @private
 */
class RoadReportTypeStruct extends ReportTypeStruct {
    constructor() {
        super(Common.ReportType.ROAD);
    }
}

/**
 * MileageReportTypeStruct - server side type
 * @private
 */
class MileageReportTypeStruct extends ReportTypeStruct {
    THoursWithSeconds: boolean;
    constructor() {
        super(Common.ReportType.MILEAGE);
        this.THoursWithSeconds = false;
    }
}

/**
 * DetailReportTypeStruct - server side type
 * @private
 */
class DetailReportTypeStruct extends ReportTypeStruct {
    SeparateHeadingFromRoute: boolean;
    SegmentEndpoints: boolean;
    constructor() {
        super(Common.ReportType.DETAIL);
        this.SeparateHeadingFromRoute = false;
        this.SegmentEndpoints = false;
    }
}

/**
 * DirectionsReportTypeStruct - server side type
 * @private
 */
class DirectionsReportTypeStruct extends ReportTypeStruct {
    CondenseDirections: boolean;
    constructor() {
        super(Common.ReportType.DIRECTIONS);
        this.CondenseDirections = false;
    }
}

/**
 * StateReportTypeStruct - server side type
 * @private
 */
class StateReportTypeStruct extends ReportTypeStruct {
    SortByRoute: boolean;
    constructor() {
        super(Common.ReportType.STATE);
        this.SortByRoute = false;
    }
}

/**
 * TollDetailReportTypeStruct - server side type
 * @private
 */
class TollDetailReportTypeStruct extends ReportTypeStruct {
    constructor() {
        super(Common.ReportType.TOLL);
    }
}

/**
 * ReduceTripRequestStruct - server side type
 * @private
 */
type ReduceTripRequestStruct = {
    request: {
        RoutePings: Array<Coordinates>;
        OffRouteMiles?: number;
        HighwayOnly?: boolean;
        ReportType?: ReportTypeStruct;
        Region?: DataRegionEnum;
        RoutingOptions?: RouteOptionsStruct; // RoutingOptions, not Options.
        ExtendedOptions?: SharedOptionsStruct;
    };
}

/**
 * PostRouteOptionsStruct - server side type
 * @private
 */
type PostRouteOptionsStruct = {
    dataVersion: DataVersionEnum;
    request: {
        // IncludePLIDs: false, // not in route options currently
        Route: {
            RouteId?: string;
            Stops: Array<StopLocationStruct>;
            AssetId?: string;
            // FuelOptions // Omitted util user request. This reqires fuel provider login information.
            ExtendedOptions?: SharedOptionsStruct;
            Options?: RouteOptionsStruct;
        };
    };
}

/**
 * PostReportOptionsStruct - server side type
 * @private
 */
type PostReportOptionsStruct = {
    dataVersion: DataVersionEnum;
    request: {
        ReportRoutes: [{
            ReportingOptions?: {
                RouteCosts?: {
                    FuelEconomyLoaded?: number;
                    FuelEconomyEmpty?: number;
                    PricePerFuelUnit?: number;
                    GreenHouseGas?: number;
                    OtherCostPerDistUnitLoaded?: number;
                    OtherCostPerDistanceUnitEmpty?: number;
                    CostTimeLoaded?: number;
                    CostTimeEmpty?: number;
                    TruckStyle?: TruckConfigEnum; // yes, two times
                };
                // TimeCosts, // DEPRECATED. Use HosOptions instead.
                IncludeFerryDistance?: boolean;
                FuelUnits?: FuelUnitEnum;
                UseTollData?: boolean;
                TollCurrency?: TollCurrencyEnum;
                ExchangeRate?: number;
                TollDiscount?: string;
                UseCustomRoadSpeeds?: boolean;
                EstimatedTimeOptions?: EstimatedTimeOptionsStruct;
                UseTraffic?: boolean;
                TruckStyle?: TruckConfigEnum;
                IncludeVehicleRestrictedCleanupPoints?: boolean;
            };
            ReportTypes?: Array<Object>;

            RouteId?: string;
            Stops: Array<StopLocationStruct>;
            AssetId?: string;
            // FuelOptions, // missing here
            Options?: RouteOptionsStruct;
        }];
    }
}

export type ReduceTripOptions = {
    routePings: Array<LngLat>;
    offRouteMiles?: number;
    highwayOnly?: boolean; // This value will be ignored if routingOptions are supplied.
    reportType?: ReportTypeEnum;
    region?: RegionEnum;
    routingOptions?: RouteOptionsBase; // highwayOnly can be found in this object too.
    extendedOptions?: SharedOptions;
    callback: Callback<{data: any}>;
}

/**
 * Convert routing options to server side format
 * @param {RouteOptionsBase} options Route options
 * @returns {RouteOptionsStruct} Routing options
 *
 * @private
 */
function _createRoutingOptions(options: RouteOptionsBase): RouteOptionsStruct {
    let truckCfg;
    if (options.trkLCV !== undefined || options.trkUnits !== undefined || options.trkHeight !== undefined ||
        options.trkLength !== undefined || options.trkWidth !== undefined ||
        options.trkMaxWeightPerAxleGroup !== undefined || options.trkTireCount !== undefined ||
        options.trkWeight !== undefined || options.trkAxles !== undefined) {
        truckCfg = {
            LCV: options.trkLCV,
            Units: options.trkUnits,
            Height: options.trkHeight,
            Length: options.trkLength,
            Width: options.trkWidth,
            Weight: options.trkWeight,
            Axles: options.trkAxles,
            MaxWeightPerAxleGroup: options.trkMaxWeightPerAxleGroup,
            TireCount: options.trkTireCount
        };
    }

    let engine;
    if (options.isHybrid !== undefined || options.emiType !== undefined ||
        options.minPoll !== undefined) {
        engine = {
            Hybrid: options.isHybrid,
            EmissionType: options.emiType,
            MinPollutionVeh: options.minPoll
        };
    }

    let trailerCfg;
    if (options.trlType !== undefined || options.trlCount !== undefined ||
        options.trlMaxHt !== undefined || options.trlMaxWt !== undefined ||
        options.trlMaxAxles !== undefined || engine !== undefined) {
        trailerCfg = {
            TypeOfTrailer: options.trlType,
            Count: options.trlCount,
            MaxHeight: options.trlMaxHt,
            MaxWeight: options.trlMaxWt,
            MaxAxles: options.trlMaxAxles,
            Engine: engine
        };
    }

    let restrictionZones;
    if (options.congestionZone !== undefined || options.lowEmissionZone !== undefined || options.ultraLowEmissionZone !== undefined) {
        restrictionZones = {
            CongestionZone: options.congestionZone,
            LowEmissionZone: options.lowEmissionZone,
            UltraLowEmissionZone: options.ultraLowEmissionZone
        };
    }

    let customBreakDurations;
    if (options.hosShortBreakDuration !== undefined ||
        options.hosEndOfDutyBreakDuration !== undefined ||
        options.hosCycleResetBreakDuration !== undefined ||
        options.hosDriverSwitchBreakDuration !== undefined ||
        options.hosBorderCrossingWaitDuration !== undefined ||
        options.hosBreakTimeInDayDuration !== undefined ||
        options.hosBreakTimeInWeekDuration !== undefined) {
        customBreakDurations = {
            ShortBreakDuration: options.hosShortBreakDuration,
            EndOfDutyBreakDuration: options.hosEndOfDutyBreakDuration,
            CycleResetBreakDuration: options.hosCycleResetBreakDuration,
            DriverSwitchBreakDuration: options.hosDriverSwitchBreakDuration,
            BorderCrossingWaitDuration: options.hosBorderCrossingWaitDuration,
            BreakTimeInDayDuration: options.hosBreakTimeInDayDuration,
            BreakTimeInWeekDuration: options.hosBreakTimeInWeekDuration,
        };
    }

    let hosOptions;
    if (options.hosEnabled !== undefined || options.hosRemDriveTimeUntilBreak !== undefined ||
        options.hosRemDriveTime !== undefined || options.hosRemOnDutyTime !== undefined ||
        options.hosRuleType !== undefined || options.hosRemCycleDutyTime !== undefined ||
        options.hosCycleTimeGains !== undefined ||
        options.hosLastCycleResetDateTime !== undefined ||
        options.hosLast24HourBreakDateTime !== undefined ||
        options.hosRemainingCanadaOnDutyTime !== undefined ||
        options.hosRemainingDriveTimeUntilBreakDriver2 !== undefined ||
        options.hosRemainingDriveTimeDriver2 !== undefined ||
        options.hosRemainingOnDutyTimeDriver2 !== undefined ||
        options.hosRemainingCycleDutyTimeDriver2 !== undefined ||
        options.hosCycleTimeGainsDriver2 !== undefined ||
        options.hosLastCycleResetDateTimeDriver2 !== undefined ||
        options.hosLast24HourBreakDateTimeDriver2 !== undefined ||
        options.hosRemainingCanadaOnDutyTimeDriver2 !== undefined ||
        options.hosAlternateHoSRuleType !== undefined ||
        options.hosRemainingTimeUntilFullDayBreak !== undefined ||
        options.hosRemainingTimeUntilFullDayBreakDriver2 !== undefined ||
        options.hosRemainingDrivingTimeInWeek !== undefined ||
        options.hosRemainingOnDutyTimeInDay !== undefined ||
        options.hosRemainingConsecutiveDrivingTime !== undefined ||
        options.hosRemainingFortnightlyTime !== undefined ||
        options.hosCurrentOffDutyTime !== undefined ||
        options.hosCurrentOffDutyTimeDriver2 !== undefined ||
        options.hosUseCycleReset !== undefined ||
        options.hosTerminalDayStartTime !== undefined ||
        options.hosUseCustomPlaces !== undefined ||
        options.hosTeamDriving !== undefined ||
        customBreakDurations !== undefined ||
        options.hosMaxAlternateRestStops !== undefined ||
        options.hosSkipRestStopInsertion !== undefined ||
        options.hosActiveDriver !== undefined ||
        options.hosMaxDrivingTimeInDay !== undefined ||
        options.hosMaxOnDutyTimeInDay !== undefined ||
        options.hosMaxOnDutyTimeInWeek !== undefined ||
        options.hosAdverseConditionException !== undefined ||
        options.hosPOIStopsReturned !== undefined ||
        options.hosHOSWindowStart !== undefined ||
        options.hosHOSWindowEnd !== undefined) {
        hosOptions = {
            Enabled: options.hosEnabled,
            RemainingDriveTimeUntilBreak: options.hosRemDriveTimeUntilBreak,
            RemainingDriveTime: options.hosRemDriveTime,
            RemainingOnDutyTime: options.hosRemOnDutyTime,
            HoSRuleType: options.hosRuleType,
            RemainingCycleDutyTime: options.hosRemCycleDutyTime,
            CycleTimeGains: options.hosCycleTimeGains,
            LastCycleResetDateTime: options.hosLastCycleResetDateTime,
            Last24HourBreakDateTime: options.hosLast24HourBreakDateTime,
            RemainingCanadaOnDutyTime: options.hosRemainingCanadaOnDutyTime,
            RemainingDriveTimeUntilBreakDriver2: options.hosRemainingDriveTimeUntilBreakDriver2,
            RemainingDriveTimeDriver2: options.hosRemainingDriveTimeDriver2,
            RemainingOnDutyTimeDriver2: options.hosRemainingOnDutyTimeDriver2,
            RemainingCycleDutyTimeDriver2: options.hosRemainingCycleDutyTimeDriver2,
            CycleTimeGainsDriver2: options.hosCycleTimeGainsDriver2,
            LastCycleResetDateTimeDriver2: options.hosLastCycleResetDateTimeDriver2,
            Last24HourBreakDateTimeDriver2: options.hosLast24HourBreakDateTimeDriver2,
            RemainingCanadaOnDutyTimeDriver2: options.hosRemainingCanadaOnDutyTimeDriver2,
            AlternateHoSRuleType: options.hosAlternateHoSRuleType,
            RemainingTimeUntilFullDayBreak: options.hosRemainingTimeUntilFullDayBreak,
            RemainingTimeUntilFullDayBreakDriver2: options.hosRemainingTimeUntilFullDayBreakDriver2,
            RemainingDrivingTimeInWeek: options.hosRemainingDrivingTimeInWeek,
            RemainingOnDutyTimeInDay: options.hosRemainingOnDutyTimeInDay,
            RemainingConsecutiveDrivingTime: options.hosRemainingConsecutiveDrivingTime,
            RemainingFortnightlyTime: options.hosRemainingFortnightlyTime,
            CurrentOffDutyTime: options.hosCurrentOffDutyTime,
            CurrentOffDutyTimeDriver2: options.hosCurrentOffDutyTimeDriver2,
            UseCycleReset: options.hosUseCycleReset,
            TerminalDayStartTime: options.hosTerminalDayStartTime,
            UseCustomPlaces: options.hosUseCustomPlaces,
            TeamDriving: options.hosTeamDriving,
            CustomBreakDurations: customBreakDurations,
            MaxAlternateRestStops: options.hosMaxAlternateRestStops,
            SkipRestStopInsertion: options.hosSkipRestStopInsertion,
            ActiveDriver: options.hosActiveDriver,
            MaxDrivingTimeInDay: options.hosMaxDrivingTimeInDay,
            MaxOnDutyTimeInDay: options.hosMaxOnDutyTimeInDay,
            MaxOnDutyTimeInWeek: options.hosMaxOnDutyTimeInWeek,
            AdverseConditionException: options.hosAdverseConditionException,
            POIStopsReturned: options.hosPOIStopsReturned,
            HOSWindowStart: options.hosHOSWindowStart,
            HOSWindowEnd: options.hosHOSWindowEnd
        };
    }
    let routeOptimization;
    if (options.hubRouting !== true) {
        routeOptimization = options.routeOptimization;
    }
    const opts = {
        VehicleType: options.vehicleType,
        RoutingType: options.routeType,
        HubRouting: options.hubRouting,
        RouteOptimization: routeOptimization,
        TollDiscourage: options.tollDiscourage,
        TollRoads: options.tollRoads,
        AFSetIDs: options.afSetIDs,
        AFSetNames: options.afSetNames,
        BordersOpen: options.bordersOpen,
        OverrideRestrict: options.overrideRestrict,
        HighwayOnly: options.highwayOnly,
        HazMatType: options.hazMatType,
        HazMatTypes: options.hazMatTypes,
        TunnelCategories: options.tunnelCategories,
        CustomRoadSpeeds: options.customRoadSpeeds,
        DistanceUnits: options.distanceUnits,
        TruckCfg: truckCfg,
        // CiteInterval: options.citeInt, // for geotunnel report, but no geotunnel report is exposed.
        ElevLimit: options.elevLimit,
        FerryDiscourage: options.ferryDiscourage,
        HoSOptions: hosOptions,
        MaintainHeading: options.maintainHeading,
        SideOfStreetAdherence: options.sideOfStreetAdherence,
        GovernorSpeedLimit: options.governorSpeedLimit,
        UseAvoidsAndFavors: options.useAvoidsAndFavors,
        UseSites: options.useSites,
        VehicleProfile : options.vehicleProfile,
        Refrigerated: options.refrigerated,
        FuelType: options.fuelType,
        FuelConsumption: options.fuelConsumption,
        TrafficLookaheadDistance: options.trafficLookaheadDistance,
        AvoidState: options.avoidState,
        AvoidCountry: options.avoidCountry,
        FavorState: options.favorState,
        FavorCountry: options.favorCountry,
        IncludeTrimblePlaceIDs: options.includeTrimblePlaceIDs,
        useCrossStreetTurnAvoidance: options.useCrossStreetTurnAvoidance, // starts with lower case u.
        CongestionZonesOpen: options.congestionZonesOpen,
        LowEmissionZonesOpen: options.lowEmissionZonesOpen,
        DisplayRestrictions: options.displayRestrictions,
        profileName: options.profileName, // starts with lower case p. Null value does not cause error.
        profileId: options.profileId, // selected Profile Id of the route option invalid profile Id will cause error.
        TrailerCfg: trailerCfg, // this is only used for EU TollDetail report
        RestrictionZones: restrictionZones,
        ClassOverrides: options.overrideClass
    };
    return opts;
}

/**
 * Gets data version from region if data version is not supplied.
 * @param {RouteOptions} options Route options
 * @returns {DataVersionEnum} Data version
 *
 * @private
 */
function _getDataVersion(options: RouteOptions): DataVersionEnum {
    let dataVersion = options.dataVersion;
    if (!dataVersion) {
        switch (options.region) {
        case Common.Region.AF:
            dataVersion = Common.DataVersion.AF;
            break;
        case Common.Region.AS:
            dataVersion = Common.DataVersion.AS;
            break;
        case Common.Region.EU:
            dataVersion = Common.DataVersion.EU;
            break;
        case Common.Region.ME:
            dataVersion = Common.DataVersion.ME;
            break;
        case Common.Region.OC:
            dataVersion = Common.DataVersion.OC;
            break;
        case Common.Region.SA:
            dataVersion = Common.DataVersion.SA;
            break;
        default:
            dataVersion = Common.DataVersion.CURRENT;
        }
    }
    return dataVersion;
}

/**
 * Converts stops to POST format
 * @param {RouteOptions} options Route options
 * @returns{Array<StopLocationStruct>} Array of sevice side stop type
 *
 * @private
 */
function _createPostStops(options: RouteOptions): Array<StopLocationStruct> {
    const postStops: Array<StopLocationStruct> = [];
    options.stops.forEach(stop => {
        const {
            lngLat,
            address: Address,
            id: ID,
            costs: Costs,
            isViaPoint: IsViaPoint,
            label: Label
        } = stop;

        const Coords = (lngLat) ? CoordinatesStruct.transformLngLat(lngLat) : CoordinatesStruct.transformLngLat(stop);

        const postStop = new StopLocationStruct({
            Coords,
            Address,
            ID,
            Costs,
            IsViaPoint,
            Label
        });

        postStops.push(postStop);
    });
    return postStops;
}

/**
 * Gets day of week
 * @param {string} day Day
 * @returns {DayOfWeekEnum} Day of week
 *
 * @private
 */
function _getDayOfWeek(day: string): DayOfWeekEnum {
    let dayOfWeek = Common.DayOfWeek.SUNDAY;
    if (day === 'monday')
        dayOfWeek = Common.DayOfWeek.MONDAY;
    else if (day === 'tuesday')
        dayOfWeek = Common.DayOfWeek.TUESDAY;
    else if (day === 'wednesday')
        dayOfWeek = Common.DayOfWeek.WEDNESDAY;
    else if (day === 'thursday')
        dayOfWeek = Common.DayOfWeek.THURSDAY;
    else if (day === 'friday')
        dayOfWeek = Common.DayOfWeek.FRIDAY;
    else if (day === 'saturday')
        dayOfWeek = Common.DayOfWeek.SATURDAY;
    return dayOfWeek;
}

/**
 * Gets time zone
 * @param {string} tz Time zone label
 * @returns {TimeZoneEnum} Time zone
 *
 * @private
 */
function _getTimeZone(tz: string): TimeZoneEnum {
    let timeZone = Common.TimeZone.LOCAL;
    if (tz === 'system')
        timeZone = Common.TimeZone.SYSTEM;
    if (tz === 'hawaii')
        timeZone = Common.TimeZone.HAWAII;
    if (tz === 'alaska')
        timeZone = Common.TimeZone.ALASKA;
    if (tz === 'pacific')
        timeZone = Common.TimeZone.PACIFIC;
    if (tz === 'arizona')
        timeZone = Common.TimeZone.ARIZONA;
    if (tz === 'mountain')
        timeZone = Common.TimeZone.MOUNTAIN;
    if (tz === 'central')
        timeZone = Common.TimeZone.CENTRAL;
    if (tz === 'eastern')
        timeZone = Common.TimeZone.EASTERN;
    if (tz === 'atlantic')
        timeZone = Common.TimeZone.ATLANTIC;
    if (tz === 'newfoundland')
        timeZone = Common.TimeZone.NEWFOUNDLAND;
    if (tz === 'gmt')
        timeZone = Common.TimeZone.GMT;
    if (tz === 'utc')
        timeZone = Common.TimeZone.UTC;
    return timeZone;
}

/**
 * Parses estimated time options in string to EstimatedTimeOptions server side type
 * @param {string} estimatedTimeOpts Estimated time options
 * @returns {EstimatedTimeOptionsStruct} EstimatedTimeOptions server side type
 *
 * @private
 */
function _parseEstimatedTimeOpts(estimatedTimeOpts: string): EstimatedTimeOptionsStruct {
    const etParts = estimatedTimeOpts.split('|');
    const etOpts: EstimatedTimeOptionsStruct = {};
    etOpts.DateOption = Common.DateOption.CURRENT;
    if (etParts[0] === 'Arrive' || etParts[0] === 'Depart') {
        etOpts.ETAETD = etParts[0] === 'Arrive' ? Common.ETAETDType.ARRIVAL : Common.ETAETDType.DEPART;
        if (etParts.length > 1) {
            const keys = Object.keys(Common.DayOfWeek).map(k => k.toLowerCase()); // [mondy, tusday, ...]
            let day = etParts[1] || ''; // 'monday'
            day = day.toLowerCase();
            if (keys.includes(day)) {
                if (etParts.length > 2) {
                    etOpts.DateOption = Common.DateOption.DAY_OF_WEEK;
                    const dtType: DateTimeTypeStruct = {};
                    dtType.DayOfWeek = _getDayOfWeek(day);
                    etOpts.DateAndTime = dtType;
                    // assume the 3rd part is time of day in (HH:mm) 24 hour format
                    etOpts.DateAndTime.TimeOfDay = etParts[2];
                } else {
                    throw new Error(`Day of week was specified, but time was not, for ex: Arrive|Monday|18:30`);
                }
            } else {
                let timeZone = Common.TimeZone.LOCAL;
                if (etParts[1]) {
                    const keys = Object.keys(Common.TimeZone).map(k => k.toLowerCase()); // [local, system, ...]
                    let tz = etParts[2] || ''; // local
                    tz = tz.toLowerCase();
                    if (etParts.length === 3 && !keys.includes(tz)) {
                        throw new Error(`Invalid TimeZone specified.`);
                    }
                    timeZone = _getTimeZone(tz);
                    const specificTime = new Date(etParts[1]); // new Date("2015-03-25T12:00:00-06:00");
                    etOpts.DateOption = Common.DateOption.SPECIFIC;

                    const st = specificTime.toLocaleTimeString('en-US'); // ??
                    const stItems = st.split(' ');
                    const timeItems = stItems[0].split(':');
                    const timeOfDay = `${timeItems[0]}:${timeItems[1]} ${stItems[1]}`;

                    const dtType: DateTimeTypeStruct = {
                        CalendarDate: specificTime.toLocaleDateString(), // 6/1/2009
                        DayOfWeek: specificTime.getDay(), // 0 - 7, sunsay
                        TimeOfDay: timeOfDay, // 8:20 AM
                        TimeZone: timeZone
                    };
                    etOpts.DateAndTime = dtType;
                } else {
                    throw new Error(`Specific time must be in the ISO8601 format, ex: 2015-03-25T12:00:00-06:00`);
                }
            }
        }
    } else {
        throw new Error(`Estimated time options must begin with Arrive or Depart.`);
    }
    return etOpts;
}

/**
 * Creates route options for POST.
 * @param {string} routeId Route id
 * @param {RouteOptions} options Route options
 * @returns {PostRouteOptionsStruct} Route options for POST
 *
 * @private
 */
function _createPostRouteOptions(routeId: string, options: RouteOptions): PostRouteOptionsStruct {
    let etOpts;
    if (options.estimatedTimeOpts !== undefined && options.estimatedTimeOpts !== null)
        etOpts = _parseEstimatedTimeOpts(options.estimatedTimeOpts);
    let routingOptions = _createRoutingOptions(options);
    const optsTemp = JSON.stringify(routingOptions);
    if (optsTemp === '{}')
        routingOptions = undefined;

    const data = {
        dataVersion: _getDataVersion(options),
        request: {
            // IncludePLIDs: false, // not in route options currently
            Route: {
                RouteId: routeId,
                Stops: _createPostStops(options),
                AssetId: options.assetId,
                // FuelOptions: {}, // missing options. Is this options still needed?
                ExtendedOptions: {
                    IncludeVehicleRestrictedCleanupPoints: options.includeVehicleRestrictedCleanupPoints,
                    EstimatedTimeOptions: etOpts,
                    UseTraffic: options.useTraffic,
                    TruckStyle: options.truckConfig
                },
                Options: routingOptions
            }
        }
    };
    return data;
}

/**
 * Creates route report options for POST.
 * @param {string} routeId Route id
 * @param {RouteOptions} options Route options
 * @returns {PostReportOptionsStruct} Route report options for POST
 *
 * @private
 */
function _createPostRouteReportOptions(routeId: string, options: RouteOptions): PostReportOptionsStruct {
    const routeOptions = _createPostRouteOptions(routeId, options);
    const route = routeOptions.request.Route;
    // Always get mileage report.
    const reportTypes = [new MileageReportTypeStruct()];
    const reports = options.reportType;
    if (reports) {
        reports.forEach((r) => {
            if (r === Common.ReportType.DIRECTIONS) {
                reportTypes.push(new DirectionsReportTypeStruct());
            } else if (r === Common.ReportType.DETAIL) {
                reportTypes.push(new DetailReportTypeStruct());
            } else if (r === Common.ReportType.STATE) {
                reportTypes.push(new StateReportTypeStruct());
            } else if (r === Common.ReportType.TOLL) {
                reportTypes.push(new TollDetailReportTypeStruct());
            } else if (r === Common.ReportType.ROAD) {
                reportTypes.push(new RoadReportTypeStruct());
            }
        });
    }

    let routeCosts;
    if (options.fuelEconLoad !== undefined || options.fuelEconEmpty !== undefined ||
        options.costPerFuelUnit !== undefined || options.costGHG !== undefined ||
        options.costMaintLoad !== undefined || options.costMaintEmpty !== undefined ||
        options.costTimeLoad !== undefined || options.costTimeEmpty !== undefined ||
        options.truckConfig !== undefined) {
        routeCosts = {
            FuelEconomyLoaded: options.fuelEconLoad,
            FuelEconomyEmpty: options.fuelEconEmpty,
            PricePerFuelUnit: options.costPerFuelUnit,
            GreenHouseGas: options.costGHG, // called greenHouseGas in 1.2
            OtherCostPerDistUnitLoaded: options.costMaintLoad,
            OtherCostPerDistanceUnitEmpty: options.costMaintEmpty,
            CostTimeLoaded: options.costTimeLoad,
            CostTimeEmpty: options.costTimeEmpty,
            TruckStyle: options.truckConfig
        };
    }
    let etOpts;
    if (options.estimatedTimeOpts !== undefined && options.estimatedTimeOpts !== null)
        etOpts = _parseEstimatedTimeOpts(options.estimatedTimeOpts);
    let reportingOptions;
    if (routeCosts !== undefined || options.inclFerryDist !== undefined ||
        options.fuelUnits !== undefined || options.inclTollData !== undefined ||
        options.tollCurrency !== undefined || options.exchangeRate !== undefined ||
        options.tollPlan !== undefined || options.custRdSpeeds !== undefined ||
        options.estimatedTimeOpts !== undefined || options.useTraffic !== undefined ||
        options.truckConfig !== undefined || options.useEUTollsReport !== undefined ||
        options.useNonNaTolls !== undefined) {
        reportingOptions = {
            RouteCosts: routeCosts,
            // TimeCosts: {}, DEPRECATED. Use HosOptions instead.
            IncludeFerryDistance: options.inclFerryDist,
            FuelUnits: options.fuelUnits,
            UseTollData: options.inclTollData,
            TollCurrency: options.tollCurrency,
            ExchangeRate: options.exchangeRate,
            TollDiscount: options.tollPlan,
            UseCustomRoadSpeeds: options.custRdSpeeds,
            EstimatedTimeOptions: etOpts,
            UseTraffic: options.useTraffic,
            TruckStyle: options.truckConfig, // yes, two times. Why PCM WS does this?
            IncludeVehicleRestrictedCleanupPoints: options.includeVehicleRestrictedCleanupPoints,
            // Language: assuming it will not be avaible for now?
            UseEUTollsReport: options.useEUTollsReport,
            UseNonNaTolls: options.useNonNaTolls,
            IncludeRoadClass: options.includeRoadClass
        };
    }

    const data = {
        dataVersion: _getDataVersion(options),
        request: {
            ReportRoutes:[{
                ReportingOptions: reportingOptions,
                ReportTypes: reportTypes,

                RouteId: route.RouteId,
                Stops: route.Stops,
                AssetId: route.AssetId,
                // FuelOptions
                Options: route.Options
            }]
        }
    };
    return data;
}

/**
 * Gets query string from options
 * @param {RouteOptions} options Route options
 * @param {string} intention Intention
 * @returns {string} Query string with options
 *
 * @private
 */
function _optionsToQueryString(options: RouteOptions, intention: 'route' | 'report'): string {
    const queryString = [];
    //stops
    queryString.push(`stops=${options.stops.map((stop) => {
        const s = LngLat.convert(stop);
        return s.toArray().join('%2C');
    }).join('%3B')}`);

    //options
    if (options.vehicleType !== undefined) {
        //check vehicle type enum
        if (!Object.values(Common.VehicleType).includes(options.vehicleType)) throw new Error(`${String(options.vehicleType)} is not a valid vehicle type. See TrimbleMaps.Common.VehicleType for accepted values.`);
        queryString.push(`vehType=${String(options.vehicleType)}`);
    }

    if (options.routeType !== undefined) {
        //check route type enum
        if (!Object.values(Common.RouteType).includes(options.routeType)) throw new Error(`${String(options.routeType)} is not a valid route type. See TrimbleMaps.Common.RouteType for accepted values.`);
        queryString.push(`routeType=${String(options.routeType)}`);
    }

    if (options.hubRouting !== undefined) {
        queryString.push(`hubRouting=${String(options.hubRouting)}`);
    }

    if (options.hubRouting !== true && options.routeOptimization !== undefined) {
        //check route optimization enum
        if (!Object.values(Common.RouteOptimization).includes(options.routeOptimization)) throw new Error(`${String(options.routeOptimization)} is not a valid route optimization type. See TrimbleMaps.Common.RouteOptimization for accepted values.`);
        queryString.push(`routeOpt=${String(options.routeOptimization)}`);
    }

    if (options.tollDiscourage !== undefined) {
        queryString.push(`avoidTolls=${String(options.tollDiscourage)}`);
    }

    if (options.tollRoads !== undefined) {
        if (!Object.values(Common.TollRoadsType).includes(options.tollRoads)) throw new Error(`${String(options.tollRoads)} is not a valid toll roads type. See TrimbleMaps.Common.TollRoadsType for accepted values.`);
        queryString.push(`tollRoads=${String(options.tollRoads)}`);
    }

    if (options.afSetIDs !== undefined && options.afSetIDs !== null) {
        if (!Array.isArray(options.afSetIDs)) {
            throw new Error('afSetIDs must be an array of numbers.');
        } else {
            if (options.afSetIDs.length > 0) {
                queryString.push(`afSetIDs=${options.afSetIDs.join(',')}`);
            }
        }
    }
    if (options.afSetNames !== undefined && options.afSetNames !== null) {
        if (!Array.isArray(options.afSetNames)) {
            throw new Error('afSetNames must be an array of names.');
        } else {
            if (options.afSetNames.length > 0) {
                queryString.push(`afSetNames=${options.afSetNames.join(',')}`);
            }
        }
    }
    if (options.bordersOpen !== undefined) {
        queryString.push(`openBorders=${String(options.bordersOpen)}`);
    }

    if (options.overrideRestrict !== undefined) { // overrideRestrict = options.overrideRestrict
        // note: in getRoutePath, the param name is overrideRestrict, in getReports, this param name is called restrOverrides
        queryString.push(`overrideRestrict=${String(options.overrideRestrict)}`);
    }

    if (options.highwayOnly !== undefined) {
        queryString.push(`hwyOnly=${String(options.highwayOnly)}`);
    }

    const hazMatTypeMessage = 'See TrimbleMaps.Common.HazMatType for accepted values.';
    if (options.hazMatType !== undefined) {
        //check hazmat enum
        if (!Object.values(Common.HazMatType).includes(options.hazMatType)) throw new Error(`${String(options.hazMatType)} is not a valid HazMat type. ${hazMatTypeMessage}`);
        queryString.push(`hazMat=${String(options.hazMatType)}`);
    }

    if (options.hazMatTypes !== undefined && options.hazMatTypes !== null) {
        if (!Array.isArray(options.hazMatTypes)) {
            throw new Error('hazMatTypes must be an array of HazMat type.');
        } else {
            if (options.hazMatTypes.length > 0) {
                const invalidItems = [];
                for (const item of options.hazMatTypes) {
                    if (!Object.values(Common.HazMatType).includes(item)) {
                        invalidItems.push(item);
                    }
                }
                if (invalidItems.length === 1) {
                    throw new Error(`${String(invalidItems[0])} is not a valid HazMat type. ${hazMatTypeMessage}`);
                } else if (invalidItems.length > 1) {
                    throw new Error(`${invalidItems.join(', ')} are not valid HazMat types. ${hazMatTypeMessage}`);
                }
                queryString.push(`hazMatTypes=${options.hazMatTypes.join(',')}`);
            }
        }
    }

    const tunnelCategoryMessage = 'See TrimbleMaps.Common.TunnelCategory for accepted values.';
    if (options.tunnelCategories !== undefined && options.tunnelCategories !== null) {
        if (!Array.isArray(options.tunnelCategories)) {
            throw new Error('tunnelCategories must be an array of TunndelCategory type.');
        } else {
            if (options.tunnelCategories.length > 0) {
                const invalidItems = [];
                for (const item of options.tunnelCategories) {
                    if (!Object.values(Common.TunnelCategory).includes(item)) {
                        invalidItems.push(item);
                    }
                }
                if (invalidItems.length === 1) {
                    throw new Error(`${String(invalidItems[0])} is not a valid TunndelCategory type. ${tunnelCategoryMessage}`);
                } else if (invalidItems.length > 1) {
                    throw new Error(`${invalidItems.join(', ')} are not valid TunndelCategory types. ${tunnelCategoryMessage}`);
                }
                queryString.push(`tunnelCategories=${options.tunnelCategories.join(',')}`);
            }
        }

    }

    // customRoadSpeeds is not supported in GET.

    if (options.distanceUnits !== undefined) {
        //check distance units enum
        if (!Object.values(Common.DistanceUnit).includes(options.distanceUnits)) throw new Error(`${String(options.distanceUnits)} is not a valid distance unit type. See TrimbleMaps.Common.DistanceUnit for accepted values.`);
        queryString.push(`distUnits=${String(options.distanceUnits)}`);
    }

    if (options.trkLCV !== undefined) {
        queryString.push(`LCV=${String(options.trkLCV)}`);
    }

    if (options.trkUnits !== undefined) {
        //check truck units enum
        if (!Object.values(Common.TruckUnit).includes(options.trkUnits)) throw new Error(`${String(options.trkUnits)} is not a valid truck unit type. See TrimbleMaps.Common.TruckUnit for accepted values.`);
        queryString.push(`vehDimUnits=${String(options.trkUnits)}`);
    }

    if (options.trkHeight !== undefined) {
        queryString.push(`vehHeight=${options.trkHeight}`);
    }

    if (options.trkLength !== undefined) {
        queryString.push(`vehLength=${options.trkLength}`);
    }

    if (options.trkWidth !== undefined) {
        queryString.push(`vehWidth=${options.trkWidth}`);
    }

    if (options.trkWeight !== undefined) {
        queryString.push(`vehWeight=${options.trkWeight}`);
    }

    if (options.trkAxles !== undefined) {
        queryString.push(`axles=${options.trkAxles}`);
    }

    if (options.trkMaxWeightPerAxleGroup !== undefined) {
        queryString.push(`maxWeightPerAxleGroup=${options.trkMaxWeightPerAxleGroup}`);
    }

    if (options.trkTireCount !== undefined) {
        queryString.push(`tireCount=${options.trkTireCount}`);
    }

    if (options.ferryDiscourage !== undefined) {
        queryString.push(`ferryDiscourage=${String(options.ferryDiscourage)}`);
    }
    if (options.inclFerryDist !== undefined) {
        queryString.push(`inclFerryDist=${String(options.inclFerryDist)}`);
    }
    if (options.fuelUnits !== undefined) {
        //check fuel units enum
        if (!Object.values(Common.FuelUnit).includes(options.fuelUnits)) throw new Error(`${String(options.fuelUnits)} is not a valid fuel unit type. See TrimbleMaps.Common.FuelUnit for accepted values.`);
        queryString.push(`fuelUnits=${String(options.fuelUnits)}`);
    }

    if (options.truckConfig !== undefined) {
        //check truck config enum
        if (!Object.values(Common.TruckConfig).includes(options.truckConfig)) throw new Error(`${String(options.truckConfig)} is not a valid truck config type. See TrimbleMaps.Common.TruckConfig for accepted values.`);
        queryString.push(`truckConfig=${String(options.truckConfig)}`);
    }

    if (options.inclTollData !== undefined) {
        queryString.push(`inclTollData=${String(options.inclTollData)}`);
    }

    if (options.fuelEconLoad !== undefined) {
        queryString.push(`fuelEconLoad=${options.fuelEconLoad}`);
    }

    if (options.fuelEconEmpty !== undefined) {
        queryString.push(`fuelEconEmpty=${options.fuelEconEmpty}`);
    }

    if (options.costPerFuelUnit !== undefined) {
        queryString.push(`costPerFuelUnit=${options.costPerFuelUnit}`);
    }

    if (options.costGHG !== undefined) {
        queryString.push(`costGHG=${options.costGHG}`);
    }

    if (options.costMaintLoad !== undefined) {
        queryString.push(`costMaintLoad=${options.costMaintLoad}`);
    }

    if (options.costMaintEmpty !== undefined) {
        queryString.push(`costMaintEmpty=${options.costMaintEmpty}`);
    }

    if (options.costTimeLoad !== undefined) {
        queryString.push(`costTimeLoad=${options.costTimeLoad}`);
    }

    if (options.costTimeEmpty !== undefined) {
        queryString.push(`costTimeEmpty=${options.costTimeEmpty}`);
    }

    if (options.tollCurrency !== undefined) {
        //check truck config enum
        if (!Object.values(Common.TollCurrency).includes(options.tollCurrency)) throw new Error(`${String(options.tollCurrency)} is not a valid toll currency. See TrimbleMaps.Common.TollCurrency for accepted values.`);
        queryString.push(`tollCurrency=${String(options.tollCurrency)}`);
    }

    if (options.exchangeRate !== undefined) {
        queryString.push(`exchangeRate=${options.exchangeRate}`);
    }

    if (options.tollPlan !== undefined) {
        queryString.push(`tollPlan=${options.tollPlan}`);
    }

    if (options.custRdSpeeds !== undefined) {
        queryString.push(`custRdSpeeds=${String(options.custRdSpeeds)}`);
    }

    if (options.citeInt !== undefined) {
        queryString.push(`citeInt=${options.citeInt}`);
    }

    if (options.region !== undefined) {
        //check region enum
        if (!Object.values(Common.Region).includes(options.region)) throw new Error(`${String(options.region)} is not a valid region. See TrimbleMaps.Common.Region for accepted values.`);
        queryString.push(`region=${String(options.region).toUpperCase()}`);
    }

    if (options.dataVersion !== undefined) {
        //check data version enum
        if (!Object.values(Common.DataVersion).includes(options.dataVersion)) throw new Error(`${String(options.dataVersion)} is not a valid data version. See TrimbleMaps.Common.DataVersion for accepted values.`);
        queryString.push(`dataVersion=${String(options.dataVersion)}`);
    }

    if (options.reportType !== undefined) {
        queryString.push(`reports=${options.reportType.join(',')}`);
    } else {
        //default to Mileage report
        queryString.push('reports=Mileage');
    }

    if (options.elevLimit !== undefined) {
        queryString.push(`elevLimit=${String(options.elevLimit)}`);
    }

    if (options.hosEnabled !== undefined) {
        queryString.push(`hosEnabled=${String(options.hosEnabled)}`);
    }

    if (options.hosRemDriveTimeUntilBreak !== undefined) {
        queryString.push(`hosRemDriveTimeUntilBreak=${String(options.hosRemDriveTimeUntilBreak)}`);
    }

    if (options.hosRemDriveTime !== undefined) {
        queryString.push(`hosRemDriveTime=${String(options.hosRemDriveTime)}`);
    }

    if (options.hosRemOnDutyTime !== undefined) {
        queryString.push(`hosRemOnDutyTime=${String(options.hosRemOnDutyTime)}`);
    }

    if (options.hosRuleType !== undefined) {
        if (!Object.values(Common.HoSScheduleType).includes(options.hosRuleType))
            throw new Error(`${String(options.hosRuleType)} is not a valid type. See TrimbleMaps.Common.HoSScheduleType for accepted values.`);
        queryString.push(`hosRuleType=${String(options.hosRuleType)}`);
    }

    if (options.hosRemCycleDutyTime !== undefined) {
        queryString.push(`hosRemCycleDutyTime=${String(options.hosRemCycleDutyTime)}`);
    }

    if (options.maintainHeading !== undefined) {
        queryString.push(`maintainHeading=${String(options.maintainHeading)}`);
    }

    if (options.sideOfStreetAdherence !== undefined) {
        if (!Object.values(Common.SideOfStreetAdherenceLevel).includes(options.sideOfStreetAdherence))
            throw new Error(`${String(options.sideOfStreetAdherence)} is not a valid type. See TrimbleMaps.Common.SideOfStreetAdherence for accepted values.`);
        queryString.push(`sideOfStreetAdherence=${String(options.sideOfStreetAdherence)}`);
    }

    if (options.governorSpeedLimit !== undefined) {
        queryString.push(`governorSpeedLimit=${String(options.governorSpeedLimit)}`);
    }

    if (options.useSites !== undefined) {
        queryString.push(`useSites=${String(options.useSites)}`);
    }

    if (options.vehicleProfile !== undefined) {
        if (!Object.values(Common.TruckConfig).includes(options.vehicleProfile))
            throw new Error(`${String(options.vehicleProfile)} is not a valid type. See TrimbleMaps.Common.TruckConfig for accepted values.`);
        queryString.push(`vehicleProfile=${String(options.vehicleProfile)}`);
    }

    if (options.useAvoidsAndFavors !== undefined) {
        queryString.push(`useAvoidsAndFavors=${String(options.useAvoidsAndFavors)}`);
    }

    if (options.refrigerated !== undefined) {
        queryString.push(`refrigerated=${String(options.refrigerated)}`);
    }

    if (options.fuelType !== undefined) {
        if (!Object.values(Common.FuelType).includes(options.fuelType))
            throw new Error(`${String(options.fuelType)} is not a valid fuel type. See TrimbleMaps.Common.FuelType for accepted values.`);
        queryString.push(`fuelType=${String(options.fuelType)}`);
    }

    if (options.fuelConsumption !== undefined) {
        queryString.push(`fuelConsumption=${String(options.fuelConsumption)}`);
    }

    if (options.trafficLookaheadDistance !== undefined) {
        queryString.push(`trafficLookaheadDistance=${String(options.trafficLookaheadDistance)}`);
    }

    if (options.avoidState !== undefined) {
        queryString.push(`avoidState=${options.avoidState.join(',')}`);
    }

    if (options.avoidCountry !== undefined) {
        queryString.push(`avoidCountry=${options.avoidCountry.join(',')}`);
    }

    if (options.favorState !== undefined) {
        queryString.push(`favorState=${options.favorState.join(',')}`);
    }

    if (options.favorCountry !== undefined) {
        queryString.push(`favorCountry=${options.favorCountry.join(',')}`);
    }

    if (options.includeTrimblePlaceIDs !== undefined) {
        queryString.push(`includeTrimblePlaceIDs=${String(options.includeTrimblePlaceIDs)}`);
    }

    if (options.useCrossStreetTurnAvoidance !== undefined) {
        queryString.push(`useCrossStreetTurnAvoidance=${String(options.useCrossStreetTurnAvoidance)}`);
    }

    if (options.congestionZonesOpen !== undefined) {
        queryString.push(`congestionZonesOpen=${String(options.congestionZonesOpen)}`);
    }

    if (options.lowEmissionZonesOpen !== undefined) {
        queryString.push(`lowEmissionZonesOpen=${String(options.lowEmissionZonesOpen)}`);
    }

    if (options.displayRestrictions !== undefined) {
        if (!Object.values(Common.DisplayRestrictionsType).includes(options.displayRestrictions))
            throw new Error(`${String(options.displayRestrictions)} is not a valid type. See TrimbleMaps.Common.DisplayRestrictionsType for accepted values.`);
        queryString.push(`displayRestrictions=${String(options.displayRestrictions)}`);
    }

    if (!options.profileName === false) { // can't be null, empty string or undefined. Null value will cause error.
        // $FlowFixMe
        queryString.push(`profileName=${options.profileName}`);
    }
    if (options.profileId !== undefined) {
        queryString.push(`profileId=${String(options.profileId)}`);
    }

    if (options.assetId !== undefined) {
        queryString.push(`assetId=${options.assetId}`);
    }

    if (options.estimatedTimeOpts !== undefined) {
        queryString.push(`estimatedTimeOpts=${options.estimatedTimeOpts}`);
    }

    if (options.useTraffic !== undefined) {
        queryString.push(`useTraffic=${String(options.useTraffic)}`);
    }

    if (options.overrideClass !== undefined) {
        queryString.push(`overrideClass=${String(options.overrideClass)}`);
    }

    if (options.congestionZone !== undefined) {
        queryString.push(`congestionZone=${String(options.congestionZone)}`);
    }

    if (options.lowEmissionZone !== undefined) {
        queryString.push(`lowEmissionZone=${String(options.lowEmissionZone)}`);
    }

    if (options.ultraLowEmissionZone !== undefined) {
        queryString.push(`ultraLowEmissionZone=${String(options.ultraLowEmissionZone)}`);
    }

    if (intention === 'report') {
        if (options.trlType !== undefined && options.trlType !== null) {
            if (Object.values(Common.TrailerType).includes(options.trlType)) {
                queryString.push(`trlType=${String(options.trlType)}`);
            } else {
                throw new Error(`${String(options.trlType)} is not a valid trailer type.`);
            }
        }
        if (options.trlCount !== undefined && options.trlCount !== null) {
            if (options.trlCount >= 0) {
                queryString.push(`trlCount=${String(options.trlCount)}`);
            } else {
                throw new Error(`${String(options.trlCount)} is not a valid value for trailer count. It must be 0 or above.`);
            }
        }
        if (options.trlMaxHt !== undefined && options.trlMaxHt !== null) {
            queryString.push(`trlMaxHt=${options.trlMaxHt}`);
        }
        if (typeof (options.trlMaxWt) === 'number' && options.trlMaxWt !== undefined && options.trlMaxWt !== null) {
            if (options.trlMaxWt > 0) {
                queryString.push(`trlMaxWt=${String(options.trlMaxWt)}`);
            } else {
                throw new Error(`${String(options.trlMaxWt)} is not a valid value for trailer max weight. It must be greater than 0.`);
            }
        }
        if (options.trlMaxAxles !== undefined && options.trlMaxAxles !== null) {
            // $FlowFixMe
            if (Number.isInteger(options.trlMaxAxles) && options.trlMaxAxles >= 1 && options.trlMaxAxles <= 8) {
                queryString.push(`trlMaxAxles=${String(options.trlMaxAxles)}`);
            } else {
                throw new Error(`${String(options.trlMaxAxles)} is not a valid value for trailer max axles. Acceptable values are 1 - 8.`);
            }
        }
        if (options.isHybrid !== undefined && options.isHybrid !== null) {
            queryString.push(`isHybrid=${String(options.isHybrid)}`);
        }
        if (options.emiType !== undefined && options.emiType !== null) {
            if (Object.values(Common.EmissionType).includes(options.emiType)) {
                queryString.push(`emiType=${String(options.emiType)}`);
            } else {
                throw new Error(`${String(options.emiType)} is not a valid emission type.`);
            }
        }
        if (options.minPoll !== undefined && options.minPoll !== null) {
            queryString.push(`minPoll=${String(options.minPoll)}`);
        }
        if (options.overrideRestrict !== undefined) {
            queryString.push(`restrOverrides=${String(options.overrideRestrict)}`);
        }
    }
    return queryString.join('&');
}

/**
 * Makes route request parameters for GET
 * @param {RouteOptions} options Route options
 * @returns {RequestParameters} Route request parameters
 *
 * @private
 */
export function getRouteRequestParameters(options: RouteOptions): RequestParameters {
    const params = _optionsToQueryString(options, 'route');
    const url = `${urls.REST_URL}/route/routePath?${params}`;
    const requestParameters = {
        url,
        headers: {
            'Authorization': getApiTokenOrKey(config)
        }
    };
    return requestParameters;
}

/**
 * Makes route request parameters for POST
 * @param {string} routeId Route id
 * @param {*} options Route options
 * @returns {RequestParameters} Route request parameters
 *
 * @private
 */
export function postRouteRequestParameters(routeId: string, options: RouteOptions): RequestParameters {
    const data = _createPostRouteOptions(routeId, options);
    if (options.dataVersion !== undefined && options.dataVersion !== null && options.dataVersion.toLocaleLowerCase().startsWith('pcm')) {
        const versionNumStr = options.dataVersion.substring(3);
        const versionNum = parseInt(versionNumStr);
        console.log(versionNum, isNaN(versionNum));
        if (!isNaN(versionNum) && options.region !== null && options.region !== undefined) {
            data.dataVersion = getRegionBasedDataVersion(options.region);
        } else {
            data.dataVersion = Common.DataVersion.CURRENT;
        }
    }
    const body = JSON.stringify(data.request);
    const url = `${urls.REST_URL}/route/routePath?dataset=${data.dataVersion}`;
    const requestParameters: RequestParameters = transformRequestPost(url, body);
    return requestParameters;
}

function getRegionBasedDataVersion(region: DataVersionEnum): DataVersionEnum {
    switch (region) {
    case Common.Region.NA:
        return Common.DataVersion.NA;
    case Common.Region.AF:
        return Common.DataVersion.AF;
    case Common.Region.AS:
        return Common.DataVersion.AS;
    case Common.Region.EU:
        return Common.DataVersion.EU;
    case Common.Region.ME:
        return Common.DataVersion.ME;
    case Common.Region.OC:
        return Common.DataVersion.OC;
    case Common.Region.SA:
        return Common.DataVersion.SA;
    default:
        return Common.DataVersion.NA;
    }
}

/**
 * Makes report request parameters for GET
 * @param {RouteOptions} options Route options
 * @returns {RequestParameters} Report request parameters
 *
 * @private
 */
export function getReportRequestParameters (options: RouteOptions): RequestParameters {
    const params = _optionsToQueryString(options, 'report');
    //build url
    const reportUrl = `${urls.REST_URL}/route/routeReports?${params}`;
    const reportRequestParameters = {
        url: reportUrl,
        headers: {
            'Authorization': getApiTokenOrKey(config)
        }
    };
    return reportRequestParameters;
}

/**
 * Makes report request paramaters for POST
 * @param {string} routeId Route id
 * @param {RouteOptions} options Route options
 * @returns {RequestParameters} Report request parameters
 *
 * @private
 */
export function postReportRequestParameters (routeId: string, options: RouteOptions) {
    const data = _createPostRouteReportOptions(routeId, options);
    if (options.dataVersion !== null && options.dataVersion !== undefined && options.dataVersion.toLocaleLowerCase().startsWith('pcm')) {
        const versionNumStr = options.dataVersion.substring(3);
        const versionNum = parseInt(versionNumStr);
        if (!isNaN(versionNum)) {
            if (data.request !== null && data.request !== undefined && data.request.ReportRoutes !== null && data.request.ReportRoutes !== undefined && data.request.ReportRoutes.length > 0 &&  data.request.ReportRoutes[0].ReportingOptions !== null && data.request.ReportRoutes[0].ReportingOptions !== undefined)
                delete data.request.ReportRoutes[0].ReportingOptions.RouteCosts;
        }
    }
    const body = JSON.stringify(data.request);
    const url = `${urls.REST_URL}/route/routeReports?dataVersion=${data.dataVersion}`;
    const reportRequestParameters: RequestParameters = transformRequestPost(url, body);
    return reportRequestParameters;
}

/**
 * Gets and validates report type for derived route and report
 * @param {ReportTypeEnum} reportType Report type
 * @returns {ReportTypeStruct} Server side report type
 *
 * @private
 */
function _getReportType(reportType: ReportTypeEnum): ReportTypeStruct {
    //Only MileageReportType and DetailReportType are supported for derived route.
    let rt;
    if (reportType === Common.ReportType.MILEAGE) {
        rt = new MileageReportTypeStruct();
    } else if (reportType === Common.ReportType.DETAIL) {
        rt = new DetailReportTypeStruct();
    } else {
        throw new Error(`${String(reportType)} is not a valid report type.`);
    }
    return rt;
}

/**
 * Gets data region
 * @param {RegionEnum} region Region
 * @returns  {DataRegionEnum} Data region
 *
 * @private
 */
function _getDataRegion(region: RegionEnum): DataRegionEnum {
    let rg;
    /* if (region === Common.Region.WW) {} */ // can't find a match
    if (region === Common.Region.AF) {
        rg = Common.DataRegion.AF;
    } else if (region === Common.Region.AS) {
        rg = Common.DataRegion.AS;
    } else if (region === Common.Region.EU) {
        rg = Common.DataRegion.EU;
    } else if (region === Common.Region.ME) {
        rg = Common.DataRegion.ME;
    } else if (region === Common.Region.NA) {
        rg = Common.DataRegion.NA;
    } else if (region === Common.Region.OC) {
        rg = Common.DataRegion.OC;
    } else if (region === Common.Region.SA) {
        rg = Common.DataRegion.SA;
    } else {
        throw new Error(`${String(region)} is not a valid region type.`);
    }
    return rg;
}

/**
 * Gets shared extendended options
 * @param {SharedOptions} options Shared options
 * @returns {SharedOptionsStruct} Server side shared options
 *
 * @private
 */
function _getExtendedOptions(options: SharedOptions): SharedOptionsStruct {
    let eTAETDType;
    let dateOption;
    const dateAndTime = {};
    const etOpts = options.estimatedTimeOptions;
    if (etOpts !== undefined && etOpts !== null) {
        if (etOpts.eTAETDType !== undefined && etOpts.eTAETDType !== null)
            eTAETDType = etOpts.eTAETDType;
        if (etOpts.dateOption !== undefined && etOpts.dateOption !== null)
            dateOption = etOpts.dateOption;
        if (etOpts.dateAndTime !== undefined && etOpts.dateAndTime !== null) {
            const calendarDate = etOpts.dateAndTime.calendarDate;
            const dayOfWeek = etOpts.dateAndTime.dayOfWeek;
            const timeOfDay = etOpts.dateAndTime.timeOfDay;
            const timeZone = etOpts.dateAndTime.timeZone;
            if (calendarDate !== undefined && calendarDate !== null)
                dateAndTime.CalendarDate = calendarDate;
            if (dayOfWeek !== undefined && dayOfWeek !== null)
                dateAndTime.DayOfWeek = dayOfWeek;
            if (timeOfDay !== undefined && timeOfDay !== null)
                dateAndTime.TimeOfDay = timeOfDay;
            if (timeZone !== undefined && timeZone !== null)
                dateAndTime.TimeZone = timeZone;
        }
    }
    const estimatedTimeOptions = {};
    if (eTAETDType !== undefined)
        estimatedTimeOptions.ETAETD = eTAETDType;
    if (dateOption !== undefined)
        estimatedTimeOptions.DateOption = dateOption;
    if (dateAndTime !== {})
        estimatedTimeOptions.DateAndTime = dateAndTime;

    const sharedOptions = {};
    if (estimatedTimeOptions !== {})
        sharedOptions.EstimatedTimeOptions = estimatedTimeOptions;
    if (options.useTraffic !== undefined && options.useTraffic !== null)
        sharedOptions.UseTraffic = options.useTraffic;
    if (options.truckStyle !== undefined && options.truckStyle !== null)
        sharedOptions.TruckStyle = options.truckStyle;
    if (options.includeVehicleRestrictedCleanupPoints !== undefined && options.includeVehicleRestrictedCleanupPoints !== null)
        sharedOptions.IncludeVehicleRestrictedCleanupPoints = options.includeVehicleRestrictedCleanupPoints;
    return sharedOptions;
}

/**
 * Creates reduce trip options for derived route
 * @param {ReduceTripOptions} options Reduce trip options
 * @returns {ReduceTripRequestStruct} Server side options
 *
 * @private
 */
function _createReduceTripOptions(options: ReduceTripOptions): ReduceTripRequestStruct {
    const rtPings = [];
    options.routePings.forEach(ping => {
        const coords = CoordinatesStruct.transformLngLat(ping);
        rtPings.push(coords);
    });
    const request = {
        RoutePings: rtPings,
        OffRouteMiles: undefined,
        HighwayOnly: undefined,
        ReportType: undefined,
        Region: undefined,
        RoutingOptions: undefined,
        ExtendedOptions: undefined
    };
    if (options.offRouteMiles !== undefined && options.offRouteMiles !== null)
        request.OffRouteMiles = options.offRouteMiles;
    if (options.highwayOnly !== undefined && options.highwayOnly !== null)
        request.HighwayOnly = options.highwayOnly;
    if (options.reportType !== undefined && options.reportType !== null)
        request.ReportType = _getReportType(options.reportType);
    if (options.region !== undefined && options.region !== null)
        request.Region = _getDataRegion(options.region);

    let routingOptions;
    if (options.routingOptions !== undefined && options.routingOptions !== null) {
        routingOptions = _createRoutingOptions(options.routingOptions);
        const optsTemp = JSON.stringify(routingOptions);
        if (optsTemp === '{}')
            routingOptions = undefined;
    }
    request.RoutingOptions = routingOptions;
    if (options.extendedOptions !== undefined && options.extendedOptions !== null) {
        const extendedOptions = _getExtendedOptions(options.extendedOptions);
        if (extendedOptions !== {})
            request.ExtendedOptions = extendedOptions;
    }
    const data = {
        request
    };
    return data;
}

/**
 * Creates reduce trip request parameters
 * @param {ReduceTripOptions} options Reduce trip options
 * @param {string} url Service endpoint
 * @returns {RequestParameters} Server side options
 *
 * @private
 */
function _createReduceTripRequestParameters(options: ReduceTripOptions, url: string): RequestParameters {
    const data = _createReduceTripOptions(options);
    const body = JSON.stringify(data.request);
    const requestParameters: RequestParameters = transformRequestPost(url, body);
    return requestParameters;
}

/**
 * Creates derived route request parameters for POST
 * @param {ReduceTripOptions} options Derived route options
 * @param {DataVersionEnum | number} dataVersion Regional data set. In PCM WS, dataset is a type of RegionalDataSet, which is similar to DataVersionEnum. For fault tolerance purpose, allowing number here.
 * @returns {RequestParameters} Service request parameters
 *
 * @private
 */
export function postReduceTripRequestParameters(options: ReduceTripOptions, dataVersion?: DataVersionEnum | number): RequestParameters {
    dataVersion = dataVersion || Common.DataVersion.CURRENT;
    const url = `${urls.REST_URL}/derivedRoute/routePath?dataset=${dataVersion}`;
    return _createReduceTripRequestParameters(options, url);
}

/**
 * Creates derived route report request parameters for POST
 * @param {ReduceTripOptions} options Derived route options
 * @param {DataVersionEnum | number} dataVersion Regional data set. In PCM WS, dataset is a type of RegionalDataSet, which is similar to DataVersionEnum. For fault tolerance purpose, allowing number here.
 * @returns {RequestParameters} Service request parameters
 *
 * @private
 */
export function postReduceTripReportRequestParameters(options: ReduceTripOptions, dataVersion?: DataVersionEnum | number): RequestParameters {
    dataVersion = dataVersion || Common.DataVersion.CURRENT;
    const url = `${urls.REST_URL}/derivedRoute/routeReports?dataset=${dataVersion}`;
    return _createReduceTripRequestParameters(options, url);
}

export type RoadSurfaceRouteOptions = {
    routeDuration: number; // The duration of the route in minutes (integer). This is required as it is used to calculate the temporal offset for each point in the route path.
    routePath: Array<LngLatLike>; // [[-74.437916,39.362518],[-74.43799,39.36264],[-74.43803,39.362718],[-74.438961,39.362649],[-74.438942,39.362469]]
    provider: string; // Optional to switch provider from "Default" to "RC1".
    callback: Callback<{data: any}>;
}

/**
 * Creates road surface route request parameters for POST
 * @param {RoadSurfaceRouteOptions} options Road surface route options
 * @returns {RequestParameters} Service request parameters
 *
 * @private
 */
export function postRoadSurfaceRouteRequestParameters(options: RoadSurfaceRouteOptions): RequestParameters {
    options = options || {};
    if (options.routeDuration === undefined || options.routeDuration === null) {
        throw new Error(`Route duration is required.`);
    }
    if (options.routePath === undefined || options.routePath === null || Array.isArray(options.routePath) !== true) {
        throw new Error(`Route path is required and must be an array.`);
    }
    const provider = options.provider || 'Default';
    if (provider !== 'Default' && provider !== 'RC1') {
        throw new Error(`Proiver needs to be "Default" or "RC1" if supplied.`);
    }
    const routePoints = [];
    options.routePath.forEach(point => {
        const coords = LngLat.convert(point).toArray();
        routePoints.push(coords);
    });
    const url = `${urls.REST_URL}/roadSurface/route?provider?provider=${provider}`;
    const body = JSON.stringify({
        routeDuration: options.routeDuration,
        routePath: routePoints
    });
    const requestParameters = transformRequestPost(url, body);
    return requestParameters;
}

// *******************************************************************************
// Rail section
// *******************************************************************************
/**
 * Rail station - server side type
 * @private
 */
type RailStationStruct = {
    Format: RailStationFormatEnum;
    Name: string;
    Railroad: string;
}

/**
 * Rail route options - server side type
 * @private
 */
type RailRouteOptionsStruct = {
    RoutingPreference: RailRoutePreferenceEnum;
    AmtrakRoutes?: boolean;
    DistUnit: RailDistanceUnitEnum;
    TerminalSwitching?: boolean;
    IntermodalOnlyStations?: boolean;
}

/**
 * Rail route request - server side type
 * @private
 */
type RailRouteRequestStruct = {
    request: {
        Stops: Array<RailStationStruct>;
        Options: RailRouteOptionsStruct;
    };
}

export type RailStation = {
    format: RailStationFormatEnum;
    name: string;
    railroad: string;
}

export type RailRouteOptions = {
    routingPreference: RailRoutePreferenceEnum;
    amtrakRoutes?: boolean;
    distUnit: RailDistanceUnitEnum;
    terminalSwitching?: boolean;
    intermodalOnlyStations?: boolean;
}

export type RailRouteRequest = {
    stops: Array<RailStation>;
    options: RailRouteOptions;
    callback: Callback<{data: any}>;
}

export type RailStationRequest = {
    splc: string;
    callback: Callback<{data: any}>;
}

/**
 * Creates rail route request parameters
 * @param {RailRouteRequest} routeRequest Rail route request parameters
 * @returns {RailRouteRequestStruct} Seriver side object
 *
 * @private
 */
function _createRailRouteRequestParameters(routeRequest: RailRouteRequest): RailRouteRequestStruct {
    const options = routeRequest.options;
    if (options === undefined || options === null)
        throw new Error(`Route options are required.`);
    if (routeRequest.stops === undefined || routeRequest.stops === null || Array.isArray(routeRequest.stops) !== true) {
        throw new Error(`Route stops must be an array.`);
    }
    const routeOptions: RailRouteOptionsStruct = {
        RoutingPreference: options.routingPreference,
        AmtrakRoutes: undefined,
        DistUnit: options.distUnit,
        TerminalSwitching: undefined,
        IntermodalOnlyStations: undefined
    };
    if (options.amtrakRoutes !== undefined && options.amtrakRoutes !== null) {
        routeOptions.AmtrakRoutes = options.amtrakRoutes;
    }
    if (options.terminalSwitching !== undefined && options.terminalSwitching !== null) {
        routeOptions.TerminalSwitching = options.terminalSwitching;
    }
    if (options.intermodalOnlyStations !== undefined && options.intermodalOnlyStations !== null) {
        routeOptions.IntermodalOnlyStations = options.intermodalOnlyStations;
    }
    const stops: Array<RailStationStruct> = [];
    routeRequest.stops.forEach(stop => {
        stops.push({
            Format: stop.format,
            Name: stop.name,
            Railroad: stop.railroad
        });
    });
    const request = {
        Stops: stops,
        Options: routeOptions
    };
    return {request};
}

/**
 * Creates rail route request parameters for POST
 * @param {RailRouteRequest} routeRequest Rail route request parameters
 * @returns {RequestParameters} Service request parameters
 *
 * @private
 */
export function postRailRouteRequestParameters(routeRequest: RailRouteRequest): RequestParameters {
    const url = `${urls.RAIL_URL}/route/path`;
    const data = _createRailRouteRequestParameters(routeRequest);
    const body = JSON.stringify(data.request);
    const requestParameters = transformRequestPost(url, body);
    return requestParameters;
}

/**
 * Creates rail station location request parameters for GET
 * @param {RailStationRequest} options Rail station location request parameters
 * @returns {RequestParameters}  Service request parameters
 *
 * @private
 */
export function getRailStationParameters(options: RailStationRequest): RequestParameters {
    if (options.splc === undefined || options.splc === null)
        throw new Error(`SPLC code is required.`);
    const urlParams = `splc=${options.splc}`;
    const url = `${urls.RAIL_URL}/poi/Station?${urlParams}`;
    const requestParameters = {
        url,
        headers: {
            'Authorization': getApiTokenOrKey(config)
        }
    };
    return requestParameters;
}
